(function () {
  const ROOT_SELECTOR = '[data-bps-home-detail-edit]';
  const GSAP_URL = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js';
  const SCROLL_TRIGGER_URL = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/ScrollTrigger.min.js';

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);

      if (existing) {
        if (existing.dataset.loaded === 'true') {
          resolve();
          return;
        }

        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.dataset.bpsDetailEditDependency = 'true';
      script.addEventListener('load', () => {
        script.dataset.loaded = 'true';
        resolve();
      }, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });
  }

  function ensureGsap() {
    if (window.gsap && window.ScrollTrigger) {
      return Promise.resolve();
    }

    if (!window.BPSDetailEditGsapPromise) {
      window.BPSDetailEditGsapPromise = loadScript(GSAP_URL).then(() => loadScript(SCROLL_TRIGGER_URL));
    }

    return window.BPSDetailEditGsapPromise;
  }

  function setActive(root, activeIndex) {
    const items = root.querySelectorAll('[data-bps-detail-item]');
    const visuals = root.querySelectorAll('[data-bps-detail-visual]');
    const maxIndex = Math.max(0, items.length - 1);
    const index = Math.min(Math.max(activeIndex, 0), maxIndex);

    root.style.setProperty('--bps-detail-active', String(index));

    items.forEach((item, itemIndex) => {
      item.classList.toggle('is-active', itemIndex === index);
    });

    visuals.forEach((visual, visualIndex) => {
      visual.classList.toggle('is-active', visualIndex === index);
    });
  }

  function initRoot(root) {
    if (!root || root.dataset.bpsDetailEditInitialized === 'true') {
      return;
    }

    root.dataset.bpsDetailEditInitialized = 'true';

    const items = root.querySelectorAll('[data-bps-detail-item]');
    const visuals = root.querySelectorAll('[data-bps-detail-visual]');
    const media = root.querySelector('[data-bps-detail-media]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    setActive(root, 0);

    items.forEach((item) => {
      const index = Number(item.dataset.bpsDetailItem || 0);

      item.addEventListener('mouseenter', () => setActive(root, index));
      item.addEventListener('focus', () => setActive(root, index));
    });

    if (reduceMotion) {
      root.classList.add('is-bps-detail-ready');
      return;
    }

    ensureGsap().then(() => {
      const gsap = window.gsap;
      const ScrollTrigger = window.ScrollTrigger;

      if (!gsap || !ScrollTrigger) {
        root.classList.add('is-bps-detail-ready');
        return;
      }

      gsap.registerPlugin(ScrollTrigger);
      root.classList.add('is-bps-detail-ready', 'is-bps-detail-gsap');

      gsap.fromTo(root.querySelectorAll('[data-bps-detail-reveal]'), {
        autoAlpha: 0,
        y: 34
      }, {
        autoAlpha: 1,
        y: 0,
        duration: 0.9,
        ease: 'power3.out',
        stagger: 0.12,
        scrollTrigger: {
          trigger: root,
          start: 'top 78%'
        }
      });

      gsap.fromTo(items, {
        autoAlpha: 0,
        x: 28
      }, {
        autoAlpha: 1,
        x: 0,
        duration: 0.72,
        ease: 'power3.out',
        stagger: 0.08,
        scrollTrigger: {
          trigger: root.querySelector('.bps-home-detail-edit__items') || root,
          start: 'top 82%'
        }
      });

      if (window.matchMedia('(min-width: 990px)').matches && visuals.length) {
        gsap.to(visuals, {
          yPercent: (index) => [-4, 2, -2, 3][index] || 0,
          scale: 1.045,
          ease: 'none',
          scrollTrigger: {
            trigger: root,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 0.8
          }
        });

        ScrollTrigger.create({
          trigger: root,
          start: 'top top',
          end: 'bottom 64%',
          onUpdate: (self) => {
            const last = Math.max(items.length - 1, 1);
            const index = Math.min(items.length - 1, Math.floor(self.progress * (last + 0.999)));
            setActive(root, index);
          },
          onEnter: () => setActive(root, 0),
          onEnterBack: () => setActive(root, 0)
        });
      }

      if (media && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        media.addEventListener('mousemove', (event) => {
          const rect = media.getBoundingClientRect();
          const x = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
          const y = ((event.clientY - rect.top) / rect.height - 0.5) * 2;

          gsap.to(media, {
            '--bps-detail-x': x.toFixed(3),
            '--bps-detail-y': y.toFixed(3),
            duration: 0.5,
            ease: 'power3.out'
          });
        });

        media.addEventListener('mouseleave', () => {
          gsap.to(media, {
            '--bps-detail-x': 0,
            '--bps-detail-y': 0,
            duration: 0.5,
            ease: 'power3.out'
          });
        });
      }
    }).catch(() => {
      root.classList.add('is-bps-detail-ready');
    });
  }

  function init(scope) {
    const rootScope = scope || document;
    rootScope.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
